import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function Handbook() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Policy", version: "1.0" });
  const [file, setFile] = useState(null);

  const load = useCallback(() => api.get("/hris/mf/handbook").then(({ data }) => setRows(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ title: "", category: "Policy", version: "1.0" }); setFile(null); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!file) { toast.error("Please choose a document to upload"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/hris/requests/upload", fd);
      await api.post("/hris/mf/handbook", {
        title: form.title.trim(), category: form.category, version: form.version,
        filename: file.name, path: up.path, url: up.url,
      });
      toast.success("Handbook document uploaded"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  const download = async (r) => {
    try {
      const { data } = await api.get(`/hris/file?path=${encodeURIComponent(r.path)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = r.filename || r.title; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const del = async (r) => { if (!window.confirm("Delete this document?")) return; try { await api.delete(`/hris/mf/handbook/${r.id}`); load(); } catch (e) { toast.error("Delete failed"); } };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Handbook" subtitle="Company handbook and policy document library." actions={
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="handbook-new-btn"><Icons.Upload className="h-4 w-4 mr-1.5" /> Upload Document</Button>
      } />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="handbook-list">
        {rows.length === 0 && <div className="col-span-full rounded-xl border border-dashed bg-white p-10 text-center text-slate-400">No handbook documents yet. Upload the first one.</div>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col" data-testid="handbook-card">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icons.BookOpen className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">{r.title}</div>
                <div className="text-xs text-slate-500 truncate">{r.filename}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant="secondary" className="text-[10px]">{r.category || "Document"}</Badge>
              <Badge variant="outline" className="text-[10px]">v{r.version || "1.0"}</Badge>
              <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(r.created_at)}</span>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => download(r)} data-testid="handbook-download"><Icons.Download className="h-4 w-4 mr-1.5" /> Download</Button>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(r)} data-testid="handbook-delete"><Icons.Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Handbook Document</DialogTitle>
            <DialogDescription>Add a policy or handbook document to the shared library.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="handbook-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label>
                <select className="w-full border rounded-lg h-10 px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="handbook-category">
                  {["Policy", "Handbook", "Code of Conduct", "Benefits", "Safety", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="handbook-version" /></div>
            </div>
            <div className="space-y-2"><Label>Document</Label><Input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="handbook-file" />{file && <span className="text-xs text-emerald-600">{file.name}</span>}</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="handbook-save">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
